import { logger } from '@/lib/utils/logger';
/**
 * Enhanced Caching Service
 * Provides multi-level caching for improved performance
 */

class CacheService {
  private static instance: CacheService;
  private memoryCache: Map<string, { data: unknown; timestamp: number; ttl: number }> = new Map();
  private defaultTTL: number = 5 * 60 * 1000; // 5 minutes default
  private static readonly MAX_ENTRIES = 100;
  
  private constructor() {
    // Periodically evict stale entries so the 100-slot cap doesn't fill up
    // with expired data and start evicting fresh entries by insertion order.
    setInterval(() => this.cleanupExpired(), 2 * 60 * 1000);
  }
  
  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }
  
  /**
   * Get cached data if available and not expired
   */
  get<T>(key: string): T | null {
    const cached = this.memoryCache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      this.memoryCache.delete(key);
      return null;
    }
    
    return cached.data as T;
  }
  
  /**
   * Set data in cache with TTL
   */
  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    this.memoryCache.set(key, { data, timestamp: Date.now(), ttl });

    // Evict oldest entry when over cap (Map preserves insertion order)
    if (this.memoryCache.size > CacheService.MAX_ENTRIES) {
      const oldestKey = this.memoryCache.keys().next().value;
      if (oldestKey !== undefined) this.memoryCache.delete(oldestKey);
    }
  }
  
  /**
   * Delete cached data
   */
  delete(key: string): void {
    this.memoryCache.delete(key);
  }

  /**
   * Delete keys matching a pattern or prefix
   */
  deletePattern(pattern: RegExp): void {
    for (const key of this.memoryCache.keys()) {
      if (pattern.test(key)) {
        this.memoryCache.delete(key);
      }
    }
  }

  /**
   * Invalidate all quotation-related cached entries
   */
  invalidateQuotations(quoteId?: string): void {
    if (quoteId) this.memoryCache.delete(`quotation_${quoteId}`);
    this.deletePattern(/^(quotation|recent_quotations)/);
  }
  
  /**
   * Clear all cache
   */
  clear(): void {
    this.memoryCache.clear();
  }
  
  /**
   * Get cache statistics
   */
  getStats(): { totalEntries: number; expiredEntries: number } {
    const now = Date.now();
    let expiredCount = 0;
    for (const entry of this.memoryCache.values()) {
      if (now - entry.timestamp > entry.ttl) expiredCount++;
    }
    return { totalEntries: this.memoryCache.size, expiredEntries: expiredCount };
  }
  
  /**
   * Clean up expired entries
   */
  private cleanupExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now - entry.timestamp > entry.ttl) this.memoryCache.delete(key);
    }
  }

  /**
   * Log cache statistics to the console.
   * Wraps getStats() for use in debug/monitoring flows.
   */
  logStats(): void {
    const stats = this.getStats();
    logger.log(`[Cache] ${stats.totalEntries} entries (${stats.expiredEntries} expired)`);
  }
  
  /**
   * Get or set with async function
   */
  async getOrSet<T>(
    key: string, 
    fetchFn: () => Promise<T>, 
    ttl: number = this.defaultTTL
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached) {
      return cached;
    }
    
    const data = await fetchFn();
    this.set(key, data, ttl);
    return data;
  }
  
  /**
   * Check if key exists and is not expired — avoids parsing the value
   */
  has(key: string): boolean {
    const entry = this.memoryCache.get(key);
    if (!entry) return false;
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.memoryCache.delete(key);
      return false;
    }
    return true;
  }

  /**
   * Invalidate cache by pattern
   */
  invalidateByPattern(pattern: string): number {
    let invalidated = 0;
    const regex = new RegExp(pattern);
    
    for (const key of this.memoryCache.keys()) {
      if (regex.test(key)) {
        this.memoryCache.delete(key);
        invalidated++;
      }
    }
    
    return invalidated;
  }

  /**
   * Invalidate all quotation-related cache entries.
   * Call after any save/delete to ensure list and detail views are fresh.
   */
  invalidateQuotations(): void {
    this.invalidateByPattern('^(quotation_|recent_quotations_)');
  }
}

export const cacheService = CacheService.getInstance();